import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip } from '@mui/joy';
import { Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getSiteDisplayName } from '../../main/utils/siteDisplayName';
import PermissionGate from '../../main/components/PermissionGate';
import { usePermissions } from '../../main/hooks/usePermissions';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { primaryColor } from '../../main/theme/constants';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import { getLocalizedUserFullName } from '../../main/utils/userDisplayName';

// Design tokens – same as CustomerHomeScreen
const pageBg = '#f8f9fc';
const cardBg = '#ffffff';
const cardBorder = '1px solid #eef0f4';
const cardShadow = '0 1px 3px rgba(0,0,0,0.04)';
const cardRadius = '16px';
const iconBg = 'rgba(95, 9, 194, 0.08)';
const textPrimary = '#2d3748';
const textSecondary = '#64748b';
const textMuted = '#94a3b8';

export default function HomeScreen() {
  const { t, i18n } = useTranslation('ui');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const { hasAnyPermission, isSuperAdmin } = usePermissions();

  const quickAccessPermissions = [
    PERMISSIONS.DASHBOARD_VIEW_ANALYTICS,
    PERMISSIONS.SHIPMENTS_LIST,
    PERMISSIONS.PRICING_RULES_LIST,
    PERMISSIONS.INVOICES_LIST,
    PERMISSIONS.CUSTOMERS_LIST,
  ];
  const showQuickAccess = isSuperAdmin() || hasAnyPermission(quickAccessPermissions);

  const getUserData = () => {
    if (user) return user;
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      try {
        return JSON.parse(storedUserData);
      } catch (e) {
        console.error('Error parsing stored user data:', e);
      }
    }
    return null;
  };

  const userData = getUserData();
  const siteName = getSiteDisplayName(settings, i18n.language);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
      <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl">
          {/* Welcome Header - colorful gradient (same layout as before) */}
          <Box
            sx={{
              marginBottom: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: cardRadius,
              padding: { xs: 3, md: 4 },
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: cardShadow,
            }}
          >
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', zIndex: 0 }} />
            <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 0 }} />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                level="h2"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: 0.5,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                {t('home.welcomeUser', {
                  name: getLocalizedUserFullName(userData, i18n.language) || t('actions.user'),
                })}
              </Typography>
              <Typography level="body1" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', marginBottom: 2 }}>
                {siteName}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {user && user.roles && user.roles.length > 0 ? (
                  user.roles.map((role, index) => (
                    <Chip
                      key={role.id || index}
                      size="sm"
                      variant="soft"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        fontWeight: 500,
                      }}
                    >
                      {role.display_name || role.name.replace('_', ' ').toUpperCase()}
                    </Chip>
                  ))
                ) : userData && userData.role ? (
                  <Chip size="sm" variant="soft" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                    {userData.role.replace('_', ' ').toUpperCase()}
                  </Chip>
                ) : (
                  <Chip size="sm" variant="soft" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                    {t('home.userChip')}
                  </Chip>
                )}
              </Box>
            </Box>
          </Box>

          {/* Quick Access - light cards (same style as CustomerHomeScreen) */}
          {showQuickAccess && (
          <>
          <Typography level="h3" sx={{ marginBottom: 2, fontWeight: 600, color: textPrimary, fontSize: '1.25rem' }}>
            {t('home.quickAccess')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <PermissionGate permissions={[PERMISSIONS.DASHBOARD_VIEW_ANALYTICS]}>
              <Grid xs={12} md={6} lg={4}>
                <Card
                  variant="plain"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    bgcolor: cardBg,
                    boxShadow: cardShadow,
                    borderRadius: cardRadius,
                    border: cardBorder,
                    transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: primaryColor,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate('/dashboard')}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DashboardIcon sx={{ fontSize: 20, color: '#e91e63' }} />
                      </Box>
                      <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {t('nav.dashboard')}
                      </Typography>
                    </Box>
                    <Typography level="body2" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                      {t('home.dashboardDesc')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </PermissionGate>

            <PermissionGate permissions={[PERMISSIONS.SHIPMENTS_LIST]}>
              <Grid xs={12} md={6} lg={4}>
                <Card
                  variant="plain"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    bgcolor: cardBg,
                    boxShadow: cardShadow,
                    borderRadius: cardRadius,
                    border: cardBorder,
                    transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: primaryColor,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate('/Shipments')}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LocalShippingIcon sx={{ fontSize: 20, color: primaryColor }} />
                      </Box>
                      <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {t('nav.shipments')}
                      </Typography>
                    </Box>
                    <Typography level="body2" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                      {t('home.shipmentsDesc')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </PermissionGate>

            <PermissionGate permissions={[PERMISSIONS.PRICING_PLANS_LIST]}>
              <Grid xs={12} md={6} lg={4}>
                <Card
                  variant="plain"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    bgcolor: cardBg,
                    boxShadow: cardShadow,
                    borderRadius: cardRadius,
                    border: cardBorder,
                    transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: primaryColor,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate('/PricingPlans')}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AttachMoneyIcon sx={{ fontSize: 20, color: '#43a047' }} />
                      </Box>
                      <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {t('nav.pricingPlans')}
                      </Typography>
                    </Box>
                    <Typography level="body2" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                      {t('home.pricingPlansDesc')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </PermissionGate>

            <PermissionGate permissions={[PERMISSIONS.INVOICES_LIST]}>
              <Grid xs={12} md={6} lg={4}>
                <Card
                  variant="plain"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    bgcolor: cardBg,
                    boxShadow: cardShadow,
                    borderRadius: cardRadius,
                    border: cardBorder,
                    transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: primaryColor,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate('/Invoices')}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#e8f5e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                      </Box>
                      <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {t('nav.invoices')}
                      </Typography>
                    </Box>
                    <Typography level="body2" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                      {t('home.invoicesDesc')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </PermissionGate>

            <PermissionGate permissions={[PERMISSIONS.CUSTOMERS_LIST]}>
              <Grid xs={12} md={6} lg={4}>
                <Card
                  variant="plain"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    bgcolor: cardBg,
                    boxShadow: cardShadow,
                    borderRadius: cardRadius,
                    border: cardBorder,
                    transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: primaryColor,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate('/Customers')}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#f3e5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GroupIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
                      </Box>
                      <Typography level="title-md" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {t('nav.customers')}
                      </Typography>
                    </Box>
                    <Typography level="body2" sx={{ color: textSecondary, lineHeight: 1.5 }}>
                      {t('home.customersDesc')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </PermissionGate>
          </Grid>
          </>
          )}

          {/* Account Information - colorful gradient card with colored icon boxes */}
          <Typography level="h3" sx={{ marginBottom: 2, fontWeight: 600, color: textPrimary, fontSize: '1.25rem' }}>
            {t('home.accountInformation')}
          </Typography>
          <Card
            variant="plain"
            sx={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '1px solid #dee2e6',
              borderRadius: cardRadius,
              boxShadow: cardShadow,
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PersonIcon sx={{ fontSize: 20, color: primaryColor }} />
                    </Box>
                    <Box>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted }}>
                        {t('home.usernameLabel')}
                      </Typography>
                      <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>
                        {(userData && userData.username) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#f3e5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <BadgeIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted, mb: 0.5 }}>
                        {t('home.rolesLabel')}
                      </Typography>
                      {user && user.roles && user.roles.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.roles.map((role, index) => (
                            <Chip key={role.id || index} size="sm" variant="soft" sx={{ bgcolor: '#e8eaf6', color: '#5c6bc0', fontSize: '0.75rem' }}>
                              {role.display_name || role.name.replace('_', ' ').toUpperCase()}
                            </Chip>
                          ))}
                        </Box>
                      ) : userData && userData.role ? (
                        <Chip size="sm" variant="soft" sx={{ bgcolor: '#e8eaf6', color: '#5c6bc0', fontSize: '0.75rem' }}>
                          {userData.role.replace('_', ' ').toUpperCase()}
                        </Chip>
                      ) : (
                        <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>{t('actions.na')}</Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#e8f5e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <EmailIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                    </Box>
                    <Box>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted }}>
                        {t('home.emailLabel')}
                      </Typography>
                      <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>
                        {(userData && userData.email) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PersonIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                    </Box>
                    <Box>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted }}>
                        {t('home.fullNameLabel')}
                      </Typography>
                      <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>
                        {getLocalizedUserFullName(userData, i18n.language) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 20, color: '#e91e63' }} />
                    </Box>
                    <Box>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted }}>
                        {t('home.phoneLabel')}
                      </Typography>
                      <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>
                        {(userData && userData.phone_number) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#e0f2f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BadgeIcon sx={{ fontSize: 20, color: '#009688' }} />
                    </Box>
                    <Box>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted }}>
                        {t('home.idNumberLabel')}
                      </Typography>
                      <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>
                        {(userData && userData.id_number) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#f1f8e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <BusinessIcon sx={{ fontSize: 20, color: '#689f38' }} />
                    </Box>
                    <Box>
                      <Typography level="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: textMuted }}>
                        {t('home.branchLabel')}
                      </Typography>
                      <Typography level="body1" sx={{ fontWeight: 500, color: textPrimary }}>
                        {(userData && userData.branch) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}
